import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import UnoSignaturContent from '../components/UnoSignaturContent';
import BlancSignaturContent from '../components/BlancSignaturContent';
import PralineSignaturContent from '../components/PralineSignaturContent';
import { mockProducts } from '../data/mockData';

gsap.registerPlugin(ScrollTrigger);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const sectionsRef = useRef([]);

  const product = mockProducts.find((p) => p.id === id);



  useEffect(() => {
    setVisible(true);

    // Only animate on scroll, not on page load
    const timer = setTimeout(() => {
      sectionsRef.current.forEach((section, index) => {
        if (section) {
          gsap.from(section, {
            scrollTrigger: {
              trigger: section,
              start: 'top 85%',
              end: 'top 60%',
              toggleActions: 'play none none reverse',
            },
            y: 30,
            opacity: 0,
            duration: 0.8,
            ease: 'power3.out',
          });
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl mb-4">Product niet gevonden</h2>
          <button
            onClick={() => navigate('/collection')}
            className="text-sm tracking-[0.2em] uppercase hover:opacity-70"
          >
            Terug naar Collectie
          </button>
        </div>
      </div>
    );
  }

  // Show detailed content for Duo Signatur, Uno Signatur, Uno Signatur Blanc, and Praliné
  const isDuoSignatur = id === 'classic-duo';
  const isUnoSignatur = id === 'pure-hazelnut';
  const isBlancSignatur = id === 'white-chocolate';
  const isPralineSignatur = id === 'praline';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--atelier-cream)' }}>
      <Navigation />

      <div className="pt-32 pb-24 px-8">
        <div className="max-w-[1400px] mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/collection')}
            className="flex items-center space-x-2 text-sm tracking-[0.2em] uppercase hover:opacity-70 mb-12 transition-all hover:translate-x-2"
            style={{ color: 'var(--atelier-black)' }}
          >
            <ArrowLeft size={16} />
            <span>Terug naar Collectie</span>
          </button>

          {isDuoSignatur ? (
            <>
              {/* Hero Section */}
              <div
                ref={(el) => (sectionsRef.current[0] = el)}
                className="text-center mb-24"
              >
                <p className="text-sm tracking-[0.3em] uppercase mb-6" style={{ color: 'var(--atelier-gold)' }}>
                  SIGNATURE
                </p>
                <h1 className="text-6xl md:text-8xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
                  Duo Signatur
                </h1>
                <p className="text-xl md:text-2xl font-light italic max-w-3xl mx-auto" style={{ color: 'var(--atelier-grey)' }}>
                  De iconische combinatie waar het allemaal begon. Nu in zijn meest verfijnde vorm.
                </p>
              </div>

              {/* Product Image */}
              <div
                ref={(el) => (sectionsRef.current[1] = el)}
                className="max-w-2xl mx-auto mb-24"
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-auto"
                />
              </div>

              {/* Het Verhaal */}
              <section
                ref={(el) => (sectionsRef.current[2] = el)}
                className="max-w-4xl mx-auto mb-24"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-8" style={{ color: 'var(--atelier-black)' }}>
                  Het Verhaal
                </h2>
                <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                  <p>
                    In 1979 ontstond een idee dat de Nederlandse ontbijttafel voorgoed zou veranderen:
                    twee smaken, één pot. Wat begon als experiment werd een icoon.
                  </p>
                  <p className="text-xl font-light" style={{ color: 'var(--atelier-black)' }}>
                    Duo Signatur is ons eerbetoon aan dat moment.
                  </p>
                  <p>
                    Dezelfde tweekleurige magie. Dezelfde combinatie van hazelnoot-chocolade en romige vanille.
                    Maar nu bereid zoals het altijd bedoeld was — zonder compromissen, met de beste ingrediënten
                    die de wereld te bieden heeft.
                  </p>
                </div>
              </section>

              {/* De Ingrediënten */}
              <section
                ref={(el) => (sectionsRef.current[3] = el)}
                className="max-w-4xl mx-auto mb-24"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
                  De Ingrediënten
                </h2>

                <div className="space-y-12">
                  {/* Hazelnoten */}
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Hazelnoten uit Piemonte
                    </h3>
                    <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      <p>
                        De heuvels van het Italiaanse Piemonte leveren 's werelds meest gewilde hazelnoten.
                        De <em>Tonda Gentile</em> — rond, zacht, en met een ongeëvenaarde smaakdiepte.
                        Elke noot wordt met de hand geselecteerd in het smalle oogstvenster tussen eind augustus en begin oktober.
                      </p>
                      <p className="text-xl font-light italic" style={{ color: 'var(--atelier-gold)' }}>
                        Wij accepteren alleen de beste 8% van de oogst.
                      </p>
                    </div>
                  </div>

                  {/* Vanille */}
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Bourbon Vanille uit Madagascar
                    </h3>
                    <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      <p>
                        Geen extract. Geen aroma. Echte vanillestokjes uit de SAVA-regio van Madagascar,
                        waar de combinatie van tropisch klimaat en vulkanische bodem vanille produceert met
                        meer dan 200 smaakcomponenten.
                      </p>
                      <p>
                        De stokjes rijpen negen maanden aan de plant voordat ze handmatig worden geoogst en
                        gedurende zes maanden worden gefermenteerd. Het resultaat: een complexe, bloemige
                        zoetheid die kunstmatige vanille nooit kan evenaren.
                      </p>
                    </div>
                  </div>

                  {/* Cacao & Room */}
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                        Rainforest Alliance Cacao
                      </h3>
                      <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                        Onze cacao komt van gecertificeerde boerderijen waar eerlijke handel en duurzaam
                        bosbeheer de standaard zijn. Geen concessies aan de toekomst voor de smaak van vandaag.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                        Nederlandse Room
                      </h3>
                      <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                        Vers. Lokaal. Van melkveebedrijven binnen 100 kilometer van onze productie in Roosendaal.
                        De romigheid die het geheel samenbrengt.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Het Proces */}
              <section
                ref={(el) => (sectionsRef.current[4] = el)}
                className="max-w-4xl mx-auto mb-24"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
                  Het Proces
                </h2>

                <div className="space-y-12">
                  {/* Kleine Batches */}
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Kleine Batches
                    </h3>
                    <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      <p>
                        Elke batch Duo Signatur wordt in beperkte hoeveelheden bereid. Niet omdat het moet,
                        maar omdat vakmanschap tijd vraagt. Onze maîtres controleren elke fase: het roosteren
                        van de hazelnoten, het tempereren van de chocolade, het balanceren van de smaken.
                      </p>
                      <p className="text-xl font-light italic" style={{ color: 'var(--atelier-black)' }}>
                        Massaproductie levert consistentie. Kleine batches leveren excellentie.
                      </p>
                    </div>
                  </div>

                  {/* De Swirl */}
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      De Swirl
                    </h3>
                    <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      <p>
                        Het hart van Duo Signatur is de scheiding — twee smaken die elkaar raken maar nooit mengen.
                        Deze techniek, geperfectioneerd over vijf decennia, vereist precisie op de milliseconde.
                        Te snel en de smaken vloeien ineen. Te langzaam en de textuur lijdt.
                      </p>
                      <div className="bg-white/50 p-6 rounded">
                        <p className="text-2xl font-light mb-2" style={{ color: 'var(--atelier-black)' }}>
                          Wij vullen 47 potten per minuut.
                        </p>
                        <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
                          Industriële lijnen vullen er 400.
                        </p>
                        <p className="text-xl font-light italic mt-4" style={{ color: 'var(--atelier-gold)' }}>
                          Het verschil proef je.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Glazen Pot */}
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Glazen Pot, Gouden Deksel
                    </h3>
                    <p className="text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      Geen plastic. De glazen pot bewaart de smaak zoals bedoeld en is volledig recyclebaar.
                      Het gouden deksel is ons handtekening — een subtiele belofte van wat binnenin wacht.
                    </p>
                  </div>
                </div>
              </section>

              {/* Proefnotities */}
              <section
                ref={(el) => (sectionsRef.current[5] = el)}
                className="max-w-4xl mx-auto mb-24 bg-white/50 p-12 rounded"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
                  Proefnotities
                </h2>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
                      Eerste indruk
                    </h4>
                    <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
                      Intense hazelnoot, geroosterd en warm.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
                      Middentonen
                    </h4>
                    <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
                      De vanille ontvouwt zich — eerst bloemig, dan romig. De chocolade brengt diepte zonder te overheersen.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
                      Afdronk
                    </h4>
                    <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
                      Lang en zijdezacht. Een vleugje karamel van de geroosterde noten.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium mb-2" style={{ color: 'var(--atelier-gold)' }}>
                      Textuur
                    </h4>
                    <p className="text-lg" style={{ color: 'var(--atelier-grey)' }}>
                      Fluweelzacht, smelt op lichaamstemperatuur. Smeerbaar maar niet vloeibaar.
                    </p>
                  </div>
                </div>
              </section>

              {/* Hoe te Serveren */}
              <section
                ref={(el) => (sectionsRef.current[6] = el)}
                className="max-w-4xl mx-auto mb-24"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
                  Hoe te Serveren
                </h2>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Het Perfecte Moment
                    </h3>
                    <div className="space-y-4 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                      <p>
                        Een verse croissant, nog warm van de oven. Een dun laagje roomboter.
                        Dan Duo Signatur — royaal, zodat beide kleuren zichtbaar zijn.
                      </p>
                      <p className="text-xl font-light italic" style={{ color: 'var(--atelier-gold)' }}>
                        Neem de tijd. Dit is geen ontbijt. Dit is een ritueel.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-2xl font-light mb-4" style={{ color: 'var(--atelier-black)' }}>
                      Alternatieve Suggesties
                    </h3>
                    <ul className="space-y-2 text-lg" style={{ color: 'var(--atelier-grey)' }}>
                      <li>• Op brioche met verse frambozen</li>
                      <li>• Door warme havermout als finishing touch</li>
                      <li>• Als vulling voor verse crêpes</li>
                      <li>• Puur, met een espresso erbij</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Specificaties */}
              <section
                ref={(el) => (sectionsRef.current[7] = el)}
                className="max-w-4xl mx-auto mb-24"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mb-8" />
                <h2 className="text-4xl md:text-5xl font-light mb-12" style={{ color: 'var(--atelier-black)' }}>
                  Specificaties
                </h2>

                <div className="bg-white/50 p-8 rounded">
                  <table className="w-full">
                    <tbody className="space-y-4">
                      {[
                        ['Inhoud', '300g'],
                        ['Herkomst hazelnoten', 'Piemonte, Italië'],
                        ['Herkomst vanille', 'SAVA-regio, Madagascar'],
                        ['Cacao certificering', 'Rainforest Alliance'],
                        ['Productie', 'Roosendaal, Nederland'],
                        ['Houdbaarheid', '12 maanden ongeopend'],
                        ['Bewaaradvies', 'Koel en droog, na openen binnen 8 weken'],
                      ].map(([key, value]) => (
                        <tr key={key} className="border-b border-gray-200">
                          <td className="py-4 text-lg font-light" style={{ color: 'var(--atelier-black)' }}>
                            {key}
                          </td>
                          <td className="py-4 text-lg text-right" style={{ color: 'var(--atelier-grey)' }}>
                            {value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* De Belofte */}
              <section
                ref={(el) => (sectionsRef.current[8] = el)}
                className="max-w-4xl mx-auto mb-24 text-center"
              >
                <div className="w-24 h-px bg-[var(--atelier-gold)] mx-auto mb-12" />
                <div className="space-y-6 text-lg leading-relaxed" style={{ color: 'var(--atelier-grey)' }}>
                  <p className="text-2xl font-light" style={{ color: 'var(--atelier-black)' }}>
                    Duo Signatur is niet zomaar een product.
                  </p>
                  <p className="text-xl">
                    Het is vijftig jaar vakmanschap, gedistilleerd in één pot. Het is het bewijs dat 'beter'
                    altijd mogelijk is — als je bereid bent nergens op te bezuinigen.
                  </p>
                  <p className="text-3xl font-light italic mt-8" style={{ color: 'var(--atelier-gold)' }}>
                    Eén hap. Dan weet je het.
                  </p>
                </div>

                <div className="mt-16">
                  <p className="text-xl font-light italic mb-4" style={{ color: 'var(--atelier-grey)' }}>
                    "De iconische combinatie die alles begon. Nu zoals het altijd bedoeld was."
                  </p>
                  <p className="text-2xl font-light" style={{ color: 'var(--atelier-black)' }}>
                    Duo Signatur. Het Origineel. Verheven.
                  </p>
                </div>
              </section>

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/collection')}
                  className="btn-primary"
                >
                  Ontdek Andere Varianten
                </button>
              </div>
            </>
          ) : isUnoSignatur ? (
            <>
              <UnoSignaturContent product={product} sectionsRef={sectionsRef} />

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/collection')}
                  className="btn-primary"
                >
                  Ontdek Andere Varianten
                </button>
              </div>
            </>
          ) : isBlancSignatur ? (
            <>
              <BlancSignaturContent product={product} sectionsRef={sectionsRef} />

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/collection')}
                  className="btn-primary"
                >
                  Ontdek Andere Varianten
                </button>
              </div>
            </>
          ) : isPralineSignatur ? (
            <>
              <PralineSignaturContent product={product} sectionsRef={sectionsRef} />

              {/* CTA */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/collection')}
                  className="btn-primary"
                >
                  Ontdek Andere Varianten
                </button>
              </div>
            </>
          ) : (
            // Simplified version for other products
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="relative aspect-[3/4] bg-neutral-50 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                {product.isNew && (
                  <div className="absolute top-6 right-6 px-4 py-2 text-xs tracking-widest" style={{ backgroundColor: 'var(--atelier-gold)', color: 'var(--atelier-black)' }}>
                    NEW
                  </div>
                )}
              </div>

              <div className="lg:pt-8">
                <span className="text-sm tracking-[0.2em] uppercase mb-4 block" style={{ color: 'var(--atelier-grey)' }}>
                  {product.category}
                </span>
                <h1 className="text-5xl lg:text-6xl font-light mb-8 tracking-wide" style={{ color: 'var(--atelier-black)' }}>
                  {product.name}
                </h1>
                <p className="text-gray-700 leading-relaxed mb-6 text-lg">
                  {product.description}
                </p>
                <p className="text-gray-600 leading-relaxed mb-12">
                  {product.details}
                </p>

                <div className="mb-12 pb-12 border-b" style={{ borderColor: 'var(--atelier-gold-light)' }}>
                  <h3 className="text-xl mb-4 tracking-wide" style={{ color: 'var(--atelier-black)' }}>Ingrediënten</h3>
                  <ul className="space-y-2">
                    {product.ingredients.map((ingredient, index) => (
                      <li key={index} className="text-sm flex items-start" style={{ color: 'var(--atelier-grey)' }}>
                        <span className="mr-2">•</span>
                        <span>{ingredient}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button className="w-full px-8 py-4 text-sm tracking-[0.2em] uppercase transition-all duration-500" style={{ backgroundColor: 'var(--atelier-black)', color: 'var(--atelier-white)' }}>
                  Waar te Koop
                </button>

                <div className="mt-12 pt-12 border-t space-y-4 text-sm" style={{ borderColor: 'var(--atelier-gold-light)', color: 'var(--atelier-grey)' }}>
                  <p>• Handcrafted in the Netherlands</p>
                  <p>• Small-batch production</p>
                  <p>• Premium ingredients, ethically sourced</p>
                  <p>• Available at select retailers</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;